import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { App, Button, Form, Spin } from 'antd';
import { BranchesOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../utils/feedback';
import { authService } from '../services/auth';
import type { Product, ProductVariant } from '../types/pz';
import { getApiErrorMessage } from '../utils/apiError';
import {
  createProduct,
  deleteVariant,
  fetchProduct as fetchProductApi,
  updateProduct,
  type ProductFormValues,
} from '../modules/products/api';
import {
  canDeleteProductCatalogItem,
  canManageProductCatalog,
} from '../modules/products/productCatalogAccessPolicy';
import {
  ProductCategory,
  categoryErrorMessage,
  fetchProductCategoryTags,
} from '../modules/products/productCategoriesApi';
import {
  getProductDataGaps,
  toCountInPiecesOnlyFormValue,
  toLogisticsFormValues,
} from '../modules/products/productLogistics';
import { toProductCopyFormValues } from '../modules/products/productCopy';
import ProductVariantModal from '../components/inventory/ProductVariantModal';
import { getProductVariantName } from '../utils/productSelectorLabel';
import IdentitySection from '../modules/products/card/IdentitySection';
import PieceSection from '../modules/products/card/PieceSection';
import AverageCostSection from '../modules/products/card/AverageCostSection';
import VariantsSection from '../modules/products/card/VariantsSection';
import CardSectionHeader from '../modules/products/card/CardSectionHeader';
import ProductCardHeader from '../modules/products/card/ProductCardHeader';
import { useBackNavigation } from '../hooks/useBackNavigation';
import '../components/layout/ObjectCard.css';
import './ProductDetail.css';

// Stable backend codes of the average-cost rules (productService.applyAverageCostChanges).
const SAVE_ERROR_MESSAGES: Record<string, string> = {
  PRODUCT_AVERAGE_COST_UNIT_REQUIRED: 'products:card.errors.unitRequiredForVariants',
  PRODUCT_AVERAGE_COST_UNIT_CHANGED_WITH_VARIANTS: 'products:card.errors.unitChangedWithVariants',
  PRODUCT_VARIANT_AVERAGE_COST_UNKNOWN_VARIANT: 'products:card.errors.unknownVariant',
};

const realVariantsOf = (product: Product | null) => (product?.variants || []).filter((variant) => !variant.isDefault);

export function toProductFormValues(product: Product | null): ProductFormValues {
  if (!product) {
    return {
      name: '',
      productCategoryId: null,
      isActive: true,
      countInPiecesOnly: false,
      averagePurchasePricePLN: null,
      averagePurchasePriceUnit: 'm2',
      averagePurchasePriceCurrency: 'PLN',
      variantCosts: {},
    };
  }
  return {
    name: product.name,
    productCategoryId: product.productCategoryId ?? null,
    ean: product.ean ?? null,
    cnCode: product.cnCode ?? null,
    englishName: product.englishName ?? null,
    isActive: product.isActive !== false,
    ...toLogisticsFormValues(product),
    countInPiecesOnly: toCountInPiecesOnlyFormValue(product),
    averagePurchasePricePLN: product.averagePurchasePricePLN ?? null,
    averagePurchasePriceUnit: product.averagePurchasePriceUnit ?? 'm2',
    averagePurchasePriceCurrency: product.averagePurchasePriceCurrency ?? 'PLN',
    variantCosts: Object.fromEntries(realVariantsOf(product)
      .map((variant) => [variant.id, variant.averagePurchasePricePLNOverride ?? null])),
  };
}

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const { t } = useTranslation(['products', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [form] = Form.useForm<ProductFormValues>();
  const isNewProduct = id === 'new';
  const [searchParams] = useSearchParams();
  // "Duplikuj produkt" opens /app/products/new?copyFrom=<id>.
  const copyFromId = isNewProduct ? searchParams.get('copyFrom') : null;

  const [loading, setLoading] = useState(!isNewProduct);
  const [saving, setSaving] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [categoryTags, setCategoryTags] = useState<ProductCategory[]>([]);
  const [dirty, setDirty] = useState(false);
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [editingVariant, setEditingVariant] = useState<ProductVariant | null>(null);

  const currentUser = authService.getCurrentUser();
  const canEdit = canManageProductCatalog(currentUser);
  const canDeleteVariant = canDeleteProductCatalogItem(currentUser);
  const showCost = authService.canViewProductAverageCost();
  const canEditCost = authService.canEditProductAverageCost();

  const values = (Form.useWatch((all) => all, { form, preserve: true }) || {}) as ProductFormValues;
  const realVariants = useMemo(() => realVariantsOf(product), [product]);

  const applyProduct = useCallback((next: Product | null) => {
    form.resetFields();
    form.setFieldsValue(toProductFormValues(next));
    setDirty(false);
  }, [form]);

  const loadProduct = useCallback(async ({ keepEdits = false, silent = false } = {}) => {
    if (!id || isNewProduct) return;
    if (!keepEdits && !silent) setLoading(true);
    try {
      const productData = await fetchProductApi(id);
      setProduct(productData);
      if (keepEdits) {
        // A variant was added/edited while the card has unsaved edits: keep
        // them and only register the new variants as inheriting the cost.
        const costs = { ...(form.getFieldValue('variantCosts') || {}) };
        realVariantsOf(productData).forEach((variant) => {
          if (!(variant.id in costs)) costs[variant.id] = variant.averagePurchasePricePLNOverride ?? null;
        });
        form.setFieldValue('variantCosts', costs);
      } else {
        applyProduct(productData);
      }
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'products:error.fetchDetailFailed'));
      navigate('/app/products');
    } finally {
      setLoading(false);
    }
  }, [id, isNewProduct, form, applyProduct, feedback, navigate, t]);

  useEffect(() => {
    fetchProductCategoryTags()
      .then(setCategoryTags)
      .catch(() => feedback.error(t('products:productCategory.loadFailed')));
    if (isNewProduct) {
      setProduct(null);
      applyProduct(null);
      if (copyFromId) loadCopySource(copyFromId);
    } else {
      loadProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, copyFromId]);

  async function loadCopySource(sourceId: string) {
    setLoading(true);
    try {
      const source = await fetchProductApi(sourceId);
      form.setFieldsValue(toProductCopyFormValues(
        toProductFormValues(source),
        t('products:card.copyName', { name: source.name }),
      ));
      setDirty(true);
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'products:error.fetchDetailFailed'));
    } finally {
      setLoading(false);
    }
  }

  const handleDuplicate = () => {
    if (dirty) {
      feedback.warning(t('products:card.duplicateBlocked'));
      return;
    }
    navigate(`/app/products/new?copyFrom=${encodeURIComponent(id!)}`);
  };

  const handleValuesChange = () => {
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
    } catch {
      feedback.warning(t('products:card.validation.fixFields'));
      return;
    }
    const formValues = form.getFieldsValue(true) as ProductFormValues;
    const name = String(formValues.name || '').trim();
    const options = {
      includeAverageCost: canEditCost,
      variantIds: canEditCost ? realVariants.map((variant) => variant.id) : [],
    };
    setSaving(true);
    try {
      if (isNewProduct) {
        const created = await createProduct({ ...formValues, name }, options);
        feedback.success(t('products:success.created', { name }));
        setDirty(false);
        navigate(`/app/products/${created.id}`, { replace: true });
      } else {
        await updateProduct(id!, { ...formValues, name }, options);
        feedback.success(t('products:success.updated', { name }));
        await loadProduct({ silent: true });
      }
    } catch (error: any) {
      feedback.error(categoryErrorMessage(
        error,
        t,
        getApiErrorMessage(error, t, 'products:error.saveFailed', SAVE_ERROR_MESSAGES, { name })
      ), { sticky: Boolean(SAVE_ERROR_MESSAGES[error?.response?.data?.code]) });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (isNewProduct) {
      navigate('/app/products');
      return;
    }
    applyProduct(product);
  };

  const handleBack = () => {
    if (dirty && !isNewProduct) {
      feedback.warning(t('products:card.savebar.leaveBlocked'));
      return;
    }
    goBack('/app/products');
  };

  const handleDeactivateVariant = async (variant: ProductVariant) => {
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('products:confirm.deleteVariant'),
        okText: t('products:variantEditor.deactivate'),
        cancelText: t('common:cancel'),
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    const variantName = getProductVariantName(variant);
    try {
      await deleteVariant(variant.id);
      feedback.success(t('products:success.variantDeleted', { name: variantName }));
      await loadProduct({ keepEdits: dirty });
    } catch (error: any) {
      const code = error?.response?.data?.code;
      feedback.error(
        code === 'PRODUCT_VARIANT_HAS_STOCK'
          ? t('products:variantEditor.stockDeactivateBlocked')
          : t('products:error.deleteVariantFailed', { name: variantName }),
        { sticky: code === 'PRODUCT_VARIANT_HAS_STOCK' }
      );
    }
  };

  const gaps = getProductDataGaps(values, { includeCost: showCost });
  const savedUnit = product?.averagePurchasePriceUnit ?? null;
  const savedCurrency = product?.averagePurchasePriceCurrency ?? 'PLN';
  // Variant costs are amounts in the product unit and currency.
  const unitChangedWithVariantCosts = Boolean(savedUnit)
    && (values.averagePurchasePriceUnit !== savedUnit || (values.averagePurchasePriceCurrency || 'PLN') !== savedCurrency)
    && Object.values(values.variantCosts || {}).some((cost) => cost !== null && cost !== undefined);
  const disabled = !canEdit;

  return (
    <div
      className={`obj-card product-card${(dirty || isNewProduct) && canEdit ? ' obj-card--editable' : ''}`}
      data-testid="product-card"
    >
      <Spin spinning={loading} size="large">
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
        className="obj-card__stack product-card-form"
        requiredMark={false}
      >
        <ProductCardHeader
          name={values.name}
          englishName={values.englishName}
          isActive={values.isActive !== false}
          isNew={isNewProduct}
          gaps={gaps}
          category={categoryTags.find((tag) => tag.id === values.productCategoryId) ?? null}
          ean={values.ean}
          cnCode={values.cnCode}
          versionCount={realVariants.length + 1}
          onBack={handleBack}
          onDuplicate={!isNewProduct && canEdit ? handleDuplicate : undefined}
        />
        <IdentitySection
          categories={categoryTags}
          disabled={disabled}
          isNew={isNewProduct}
        />
        <div className="product-card-layout">
          <div className="product-card-stack">
            <PieceSection
              values={values}
              showHeight={values.height !== undefined}
              disabled={disabled}
              piecesOnlyEditable
            />
            {showCost && (
              <AverageCostSection
                values={values}
                disabled={disabled || !canEditCost}
                updatedByName={product?.averagePurchasePriceUpdatedByName}
                updatedAt={product?.averagePurchasePriceUpdatedAt}
                unitChangedWithVariantCosts={unitChangedWithVariantCosts}
              />
            )}
            {isNewProduct ? (
              <section className="product-card-section">
                <CardSectionHeader
                  title={t('products:card.variants.title')}
                  icon={<BranchesOutlined />}
                  tone="indigo"
                  hint={t('products:card.variants.saveFirst')}
                />
              </section>
            ) : (
              <VariantsSection
                productEan={values.ean}
                product={product}
                variants={realVariants}
                showCost={showCost}
                canEdit={canEdit && canEditCost}
                canDeactivate={canDeleteVariant}
                productPrice={values.averagePurchasePricePLN}
                productUnit={values.averagePurchasePriceUnit}
                productCurrency={values.averagePurchasePriceCurrency || 'PLN'}
                onCostChanged={() => setDirty(true)}
                onAddVariant={() => { setEditingVariant(null); setVariantModalOpen(true); }}
                onEditVariant={(variant) => { setEditingVariant(variant); setVariantModalOpen(true); }}
                onDeactivateVariant={handleDeactivateVariant}
              />
            )}
          </div>
        </div>
      </Form>
      </Spin>

      {(dirty || isNewProduct) && canEdit && (
        <div className="obj-savebar" role="region" aria-label={t('products:card.savebar.save')}>
          <span className="obj-savebar__status">
            {isNewProduct ? t('products:card.savebar.new') : t('products:card.savebar.dirty')}
          </span>
          <Button onClick={handleDiscard} disabled={saving}>{t('products:card.savebar.discard')}</Button>
          <Button type="primary" onClick={handleSave} loading={saving}>
            {t('products:card.savebar.save')}
          </Button>
        </div>
      )}

      {product && (
        <ProductVariantModal
          open={variantModalOpen}
          product={product}
          variant={editingVariant}
          onCancel={() => setVariantModalOpen(false)}
          onSaved={async () => {
            setVariantModalOpen(false);
            setEditingVariant(null);
            await loadProduct({ keepEdits: dirty });
          }}
        />
      )}
    </div>
  );
};

export default ProductDetail;
